// Content for SB 1.1.1 fifth explanation, part 5

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Srimad Bhagavatam 1.1.1',
        sloka: `oṁ namo bhagavate vāsudevāya
janmādy asya yato 'nvayād itarataś cārtheṣv abhijñaḥ svarāṭ
tene brahma hṛdā ya ādi-kavaye muhyanti yat sūrayaḥ |
tejo-vāri-mṛdāṁ yathā vinimayo yatra tri-sargo 'mṛṣā
dhāmnā svena sadā nirasta-kuhakaṁ satyaṁ paraṁ dhīmahi ||`,
        translation: `Let us meditate upon the Absolute Truth, Kṛṣṇa (<strong>satyaṁ paraṁ dhīmahi</strong>), who is the cause of creation, maintenance and destruction of this universe (<strong>janmādy asya yataḥ</strong>), as the material and efficient cause (<strong>anvayād itarataś ca</strong>), since he is the only knower of all objects (<strong>artheṣv abhijñaḥ</strong>) and the only independent being (<strong>svarāṭ</strong>); who revealed the Vedas to Brahmā within his mind (<strong>tene brahma hṛdā ya ādi-kavaye</strong>); who is difficult to understand even for the learned (<strong>muhyanti yat sūrayaḥ</strong>); whose body is thought to be made of matter due to illusion just as one mistakes fire, water and earth for each other (<strong>tejo-vāri-mṛdāṁ yathā vinimayo yatra tri-sargo ’mṛṣā</strong>); and who negates all false arguments regarding his existence by possessing a transcendental, inconceivable form, and by the strength of devotees’ experiencing him though his svarūpa-śakti (<strong>dhāmnā svena sadā nirasta-kuhakaṁ</strong>).`,
    },
    blocks: [
        {
            type: 'heading',
            headingText: 'Fifth explanation',
            content:
                'Let us meditate on the most beneficial spiritual process <strong>which is beyond the guṇas</strong>, which brings about the appearance of the Lord as Bhagavān and by which there is realization of brahman and Paramātmā in the processes of jñāna and yoga;<br /><br /><strong>which is complete knowledge, independent of other processes;</strong><br /><br />which was revealed by Nārada to Vyāsa, but <strong>which is bewildering to even great sages;</strong><br /><br />which is not mixed with the three guṇas; and <strong>which defeats its opponents by its very nature of giving direct experience of bliss.</strong>',
        },
        {
            type: 'text',
            content:
                'The process of bhakti is indicated by scriptures as the abhidheya by which the āśraya, Kṛṣṇa, can be attained. It brings about the highest realization and attracts even Kṛṣṇa. That bhakti then becomes the prayojana, prema.<br /><br /><strong>Thus bhakti-yoga should also be considered as the subject of this verse.</strong> Because of this, another meaning is included as an essential theme of this verse. Thus it is said in the Twelfth Canto:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 12.12.49-50',
                sloka: `mṛṣā giras tā hy asatīr asat-kathā na kathyate yad bhagavān adhokṣajaḥ
tad eva satyaṁ tad u haiva maṅgalaṁ tad eva puṇyaṁ bhagavad-guṇodayam
tad eva ramyaṁ ruciraṁ navaṁ navaṁ tad eva śaśvan manaso mahotsavam
tad eva śokārṇava-śoṣaṇaṁ nṛṇāṁ yad uttamaḥśloka-yaśo ’nugīyate`,
                translation:
                    'Words that do not describe the transcendental Lord but instead deal with temporary matters are simply false, harsh and useless. Only those words that manifest the transcendental qualities of the Supreme Lord are actually truthful, auspicious and pure. Those words describing the glories of the all-famous Lord are attractive, relishable and ever fresh. They are a perpetual festival for the mind, and they dry up the ocean of misery.',
            },
        },
        {
            type: 'text',
            content:
                'Taking bhakti as the subject of the verse, <strong><em>param</em>, the best, then means “beyond the three guṇas,”</strong> since bhakti is another form of the real substance (vāstava-vastu). <strong><em>Satyam</em> refers to bhakti-yoga</strong> which is full of the most auspicious qualities beneficial for spiritual practitioners (sat).<br /><br /><strong><em>Satyaṁ paraṁ dhimahi</em> then means “let us meditate on transcendental bhakti-yoga.”</strong>',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 3.29.12',
                sloka: 'lakṣaṇaṁ bhakti-yogasya nirguṇasya hy udāhṛtam',
                translation: 'It is filled with the qualities of bhakti-yoga, which is beyond the guṇas.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.29.20',
                sloka: `na hy aṅgopakrame dhvaṁso
mad-dharmasyoddhavāṇv api
mayā vyavasitaḥ samyaṅ
nirguṇatvād anāśiṣaḥ`,
                translation:
                    'O Uddhava! Because I have personally established it, this process of niṣkāma-bhakti is beyond the guṇas. Even by starting and not completing the process, there is no destruction of results.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Gopāla-tāpanī Upaniṣad 2.78',
                sloka: 'vijñāna-ghana ānanda-ghanaḥ sac-cidānandaika-rase bhakti-yoge tiṣṭhati',
                translation:
                    'The Lord full of knowledge and bliss is situated in bhakti-yoga which is also eternity, knowledge and bliss.',
            },
        },
        {
            type: 'heading',
            headingText: 'Bhakti as Independent and Complete',
            content:
                'Then the power of bhakti is described. <strong>From bhakti (<em>yataḥ</em>) there is an appearance (<em>janma</em>) of the Supreme Lord (<em>ādyasya</em>)</strong> in the worshippers as Bhagavān.<br /><br />As well, <strong>by the association of bhakti (<em>anvayāt</em>) there is realization of the Supreme Lord as brahman and Paramātmā in other processes (<em>itarataḥ artheṣu</em>)</strong> — niṣkāma-karma-yoga, jñāna-yoga and aṣṭāṅga-yoga.',
        },
        {
            type: 'dialogue',
            objection:
                '“But it is well known that cultivation of pure jñāna alone gives realization of brahman.”',
            response:
                'No. The Supreme Lord is jñāna in complete form (abhijñaḥ).<br /><br /><strong>Because jñāna is in the mode of sattva only, without the association (anvaya) of bhakti, there can be no realization of brahman or Paramātmā.</strong>',
        },
        {
            type: 'text',
            content: 'Thus it is said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.5.12',
                sloka: `naiṣkarmyam apy acyuta-bhāva-varjitaṁ
na śobhate jñānam alaṁ nirañjanam
kutaḥ punaḥ śaśvad abhadram īśvara
na cārpitaṁ karma yad apy akāraṇam`,
                translation:
                    'Even the stage of jñāna without the bondage of karma is not glorious because it is devoid of bhakti to the Supreme Lord. What is the use of having destroyed ignorance? What to speak of sakāma-karma which is suffering during practice and perfection, and niṣkāma-karma, when not offered to the Lord?',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-Gītā 18.55',
                sloka: `bhaktyā mām abhijānāti
yāvān yaś cāsmi tattvataḥ
tato māṁ tattvato jñātvā
viśate tad-anantaram`,
                translation:
                    'Only by bhakti can a person know me as Brahman. Then, knowing me as Brahman by that bhakti, he merges with Me.',
            },
        },
        {
            type: 'dialogue',
            objection:
                '”Jñāna-yoga, for realizing brahman, is dependent on bhakti, and bhakti, for realizing Bhagavān, is dependent on jñāna.”',
            response:
                'To counteract this argument, it is said that this <strong>bhakti-yoga reigns independently (<em>svarāṭ</em>)</strong>.” The king being independent is not dependent on anyone.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 2.3.10',
                sloka: `akāmaḥ sarva-kāmo vā
mokṣa-kāma udāra-dhīḥ |
tīvreṇa bhakti-yogena
yajeta puruṣaṁ param ||`,
                translation:
                    'The person desiring destruction of all desires, the person with all desires, even the person with the intense desire for liberation, if he has good intelligence, will worship the Supreme Lord with pure bhakti. .',
            },
        },
        {
            type: 'text',
            content:
                'Tivreṇa bhakti-yogena means “by pure bhakti.” <strong>Bhakti should remain unmixed with jñāna or other elements</strong>, just as the sun’s rays remain untouched by the clouds.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.20.32-33',
                sloka: `yat karmabhir yat tapasā
jñāna-vairāgyataś ca yat
yogena dāna-dharmeṇa
śreyobhir itarair api
sarvaṁ mad-bhakti-yogena
mad-bhakto labhate ’ñjasā
svargāpavargaṁ mad-dhāma
kathañcid yadi vāñchati`,
                translation:
                    'Everything that can be achieved by karma, penance, jñāna, vairāgya, mystic yoga, charity, dharma and all other auspicious means of perfecting life is easily achieved by my devotee through bhakti. If somehow or other my devotee desires Svarga, liberation, or residence in my abode, he easily achieves such benedictions.',
            },
        },
        {
            type: 'text',
            content: 'On the other hand mixed bhakti is prohibited for devotees:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.20.31',
                sloka: `tasmān mad-bhakti-yuktasya
yogino vai mad-ātmanaḥ
na jñānaṁ na ca vairāgyaṁ
prāyaḥ śreyo bhaved iha`,
                translation:
                    'Therefore, for a devotee engaged in my loving service, with mind fixed on me, the cultivation of knowledge and renunciation is generally not the means of achieving the highest perfection within this world.',
            },
        },
        {
            type: 'heading',
            headingText: 'Mercy of Devotees and Refutation of Objections',
            content:
                'However such pure bhakti cannot be attained without the mercy of devotees. This is next stated in the verse. That bhakti was <strong>revealed (<em>tene</em>)</strong> by mercy <strong>to Vyāsa (<em>ādi-kavaye</em>)</strong> by Nārada, who has Brahman (Kṛṣṇa) in his heart <strong>(<em>brahma-hṛdā</em>)</strong>.',
        },
        {
            type: 'dialogue',
            objection:
                '“But how can we understand that even omniscient Vyāsa’s knowledge of bhakti is dependent on another person (Nārada)?”',
            response:
                'Even the sages such as Vasiṣṭa are bewildered about bhakti-yoga which is beyond the guṇas.<br /><br />Because it is impossible for minds and intelligences generated from the guṇas, to understand by themselves about bhakti-yoga without the mercy of devotees, <strong>those persons only attain ignorance (<em>muhyanti</em>)</strong>. This is mentioned in the Haṁsa-guhya prayers:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 6.4.31',
                sloka: `yac-chaktayo vadatāṁ vādināṁ vai
vivāda-saṁvāda-bhuvo bhavanti
kurvanti caiṣāṁ muhur ātma-mohaṁ
tasmai namo ’nanta-guṇāya bhūmne`,
                translation:
                    'I offer respects to the great Lord of eternal qualities, whose material energy is the cause of agreement and disagreement among those who make philosophies and then argue with others, whose material energy continually bewilders them as to the real nature of ātmā.',
            },
        },
        {
            type: 'dialogue',
            objection:
                '“But bhakti is not completely beyond the guṇas. This is shown in the Third Canto where bhakti is described as sāttvika, rājasika and tāmasika.”',
            response:
                'In this bhakti (<em>yatra</em>) the idea that it is made of the three guṇas <strong>(<em>tri-sargaḥ</em>)</strong> is <strong>unfounded (<em>mṛṣā</em>)</strong>.<br /><br />Although milk is not made of fire, water of dust in its natural state, it can become burned by fire, diluted by water and contaminated with dirt.<br /><br /><strong>Similarly, bhakti-yoga, beyond the three guṇas, is called sāttvika, rājasika or tāmasika only because of the sattva, rajas and tamas possessed by persons practicing bhakti.</strong>',
        },
        {
            type: 'dialogue',
            objection: '“Many people will object if you say bhakti-yoga is beyond the guṇas.”',
            response:
                'This bhakti-yoga defeats all the arguers because of its very nature <strong>(<em>dhāmnā svena</em>)</strong> -- being filled with unprecedented sweetness that can be realized by the devotees. This means that since it is directly realized, it does not depend on any other proof to defeat the arguers.',
        },
        {
            type: 'heading',
            headingText: 'Bhāgavatam as Lamp, Sun, Fruit, and Mohinī',
            content: 'Bhāgavatam is compared to a lamp in the following two verses:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.3',
                sloka: `yaḥ svānubhāvam akhila-śruti-sāram ekam
adhyātma-dīpam atititīrṣatāṁ tamo ’ndham
saṁsāriṇāṁ karuṇayāha purāṇa-guhyaṁ
taṁ vyāsa-sūnum upayāmi guruṁ munīnām`,
                translation:
                    'I surrender to the son of Vyāsa, the incomparable guru of all the sages, who mercifully spoke the Purāṇa full of hidden meanings, the essence of all the scriptures, the essence of hearing, for all the people of this world, even in the future; who spoke the Bhāgavatam, which revealed the excellence of rasa to Śukadeva, and which is the revealer of ātmā for those desiring to cross dense ignorance with ease.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 12.13.19',
                sloka: `kasmai yena vibhāsito ’yam atulo jñāna-pradīpaḥ purā
tad-rūpeṇa ca nāradāya munaye kṛṣṇāya tad-rūpiṇā
yogīndrāya tad-ātmanātha bhagavad-rātāya kāruṇyatas
tac chuddhaṁ vimalaṁ viśokam amṛtaṁ satyaṁ paraṁ dhīmahi`,
                translation:
                    'I meditate upon that pure, spotless Nārāyaṇa, who is free from suffering and death and who previously revealed this incomparable lamp of knowledge to Brahmā. Brahmā then spoke it to the sage Nārada, who narrated it to Kṛṣṇa-dvaipāyana Vyāsa. Śrīla Vyāsa revealed this Bhāgavatam to the greatest of sages, Śukadeva Gosvāmī, and Śukadeva mercifully spoke it to Mahārāja Parīkṣit.',
            },
        },
        {
            type: 'text',
            content: 'Bhāgavatam is compared to the sun:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.3.43',
                sloka: `kṛṣṇe sva-dhāmopagate
dharma-jñānādibhiḥ saha
kalau naṣṭa-dṛśām eṣa
purāṇārko ’dhunoditaḥ`,
                translation:
                    'Though Kṛṣṇa, departing Dvārakā, arrived at Prabhāsa, and then disappeared along with his six opulences, this Purāṇa, another form of the sun, has now risen in Kali-yuga for those who have lost their knowledge.',
            },
        },
        {
            type: 'text',
            content:
                'Bhāgavatam is described as giving a tasty fruit in the third verse of this chapter with <i>nigama-kalpa-taror galitaṁ phalaṁ rasam</i>: it is the matured fruit (of rasa) of the desire tree of the Vedas. (SB 1.1.3)',
        },
        {
            type: 'text',
            content: 'Bhāgavatam is compared to Mohinī avatāra because it gives different results to different people:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 12.13.11-12',
                sloka: `ādi-madhyāvasāneṣu vairāgyākhyāna-saṁyutam
hari-līlā-kathā-vrātā-mṛtānandita-sat-suram
sarva-vedānta-sāraṁ yad brahmātmaikatva-lakṣaṇam
vastv advitīyaṁ tan-niṣṭhaṁ kaivalyaika-prayojanam`,
                translation:
                    "From beginning to end, the Śrīmad-Bhāgavatam is full of the Lord's pastimes which give bliss to the devotees, endowed with a sense of renunciation. This Bhāgavatam is the essence of all Vedānta philosophy because its subject matter is one Brahman, a substance with no duality. The main goal of the work is merging.",
            },
        },
        {
            type: 'text',
            content: '<hr class="my-8 border-gray-300 border-t-2 dark:border-gray-700" />',
        },
        {
            type: 'heading',
            headingText: 'Fivefold Synthesis',
        },
        {
            type: 'text',
            content:
                'The first explanation of this first verse of Bhāgavatam shows its nature as a <strong>light</strong> (giving deliverance from misery through general understanding of the Lord or adhyātmā).<br /><br />The second explanation shows it as the <strong>sun</strong> (more powerful, destroying misconceptions of the demons, bringing joy to the devotees).<br /><br />The third, fourth and fifth explanations show it is the bestower of <strong>tasty fruit</strong> full of rasa (revealing madhura-rasa, Rādhā and pure bhakti). The devotees, being the rightful recipients, are considered to be like the devatās, since they receive the nectar in the form of relishing the rarest taste of these five meanings.<br /><br />The Bhāgavatam is considered to be like <strong>Mohinī</strong>, serving out these different meanings of Bhāgavatam to the devotees.',
        },
        {
            type: 'text',
            content:
                'Though all twelve cantos of the Bhāgavatam are like the tasty fruit, the sun and the lamp, the topics of creation and destruction (sarga and nirodha) and various verses describing this are considered to be the <strong>lamp</strong>, since those topics reveal only the general aspect of the Lord (adhyātmā).<br /><br />Secondary creation (visarga), maintenance (sthāna), protection (poṣaṇa) and other topics (ūti, manvantara, īśānukathā, and mukti) are considered to be the <strong>sun</strong> since they reveal, in addition, the results of forbidden practices for enjoyment and renunciation available in dharma, artha, kāma, mokṣa and their unlimited sub-varieties.<br /><br />Topics dealing with the appearance and pastimes of the Lord (āśraya) and his devotees, and with bhakti and prema, are considered to be the <strong>tasty fruit of rasa</strong>. All the topics however give joy to the devotees of the Lord by taking on a meaning favorable for bhakti.<br /><br />The same topics however are like <strong>Mohinī</strong> and take on a different meaning for bewildering the demonic group who give meanings unfavorable for bhakti. One should not say that it is unbefitting to eulogize the unfavorable meanings of the Bhāgavatam which is directly filled with bhakti-rasa. The Supreme Lord is full of all śaktis and so is the Bhāgavatam. Thus it takes meanings corresponding to the minds of various types of persons (adhikāris of this śāstra), for such revelation is befitting the person (the Lord) who possesses all powers.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 10.43.17',
                sloka: `mallānām aśanir nṛṇāṁ nara-varaḥ strīṇāṁ smaro mūrtimān
gopānāṁ sva-jano ’satāṁ kṣiti-bhujāṁ śāstā sva-pitroḥ śiśuḥ
mṛtyur bhoja-pater virāḍ aviduṣāṁ tattvaṁ paraṁ yogināṁ
vṛṣṇīnāṁ para-devateti vidito raṅgaṁ gataḥ sāgrajaḥ`,
                translation:
                    'The various groups of people in the arena regarded Kṛṣṇa in different ways when He entered it with His elder brother. The wrestlers saw Kṛṣṇa as a lightning bolt, the men of Mathurā as the best of males, the women as Cupid in person, the cowherd men as their relative, the impious rulers as a chastiser, His parents as their child, the King of the Bhojas as death, the unintelligent as a material form, the yogīs as the Absolute Truth and the Vṛṣṇis as their supreme worshipable Deity.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>In the above verse it is mentioned virāḍ aviduṣām: for the foolish he appeared to be a material person. Thus everything is in agreement.</strong>',
        },
    ],
};

export default content;
